import type { IPubAds, ISlotRenderEndedEvent } from '../types/google-tag.types';

type LogErrorFn = (message: string, extra?: Record<string, unknown>) => void;

interface IMismatchStatus {
	nonEmptyAdsFromEvents: number;
	visibleAdsInDom: number;
}

const renderedNonEmptySlots = new Set<string>();
let hasSlotRenderEndedListener = false;
let validationToken = 0;
let lastLoggedMismatchStatus: IMismatchStatus | undefined;

const countVisibleAdsInDom = (): number => {
	const iframeContainers = document.querySelectorAll<HTMLElement>('article[data-banner-type] > div[id^="google_ads_iframe_"]');

	return Array.from(iframeContainers).filter((container) => {
		const article = container.parentElement;
		if (!article) return false;

		const articleIsVisible = window.getComputedStyle(article).display !== 'none';
		const renderedHeight = container.offsetHeight || Number.parseInt(container.style.height || '0', 10) || 0;

		return articleIsVisible && renderedHeight > 0;
	}).length;
};

const isSameMismatchStatus = (
	left: IMismatchStatus | undefined,
	right: IMismatchStatus,
): boolean => {
	if (!left) return false;

	return left.nonEmptyAdsFromEvents === right.nonEmptyAdsFromEvents
		&& left.visibleAdsInDom === right.visibleAdsInDom;
};

const syncParentClusterVisibility = (slotContainer: HTMLElement): void => {
	const parent = slotContainer.parentElement;
	if (!parent?.classList.contains('MultipleTopVisibilityBannersWrapper')) return;

	const articles = Array.from(parent.children).filter((child): child is HTMLElement => (
		child instanceof HTMLElement && child.tagName === 'ARTICLE'
	));
	if (articles.length === 0) return;

	const hasVisibleBanner = articles.some((article) => window.getComputedStyle(article).display !== 'none');

	if (hasVisibleBanner) {
		parent.style.removeProperty('display');
		return;
	}

	parent.style.display = 'none';
};

const applyEmptySlotVisibility = (event: ISlotRenderEndedEvent): void => {
	const slotId = event.slot?.getSlotElementId?.();
	if (!slotId) return;

	const slotContainer = document.getElementById(slotId);
	if (!slotContainer) return;

	if (event.isEmpty === true) {
		slotContainer.style.display = 'none';
		syncParentClusterVisibility(slotContainer);
		return;
	}

	if (event.isEmpty === false) {
		slotContainer.style.removeProperty('display');
		syncParentClusterVisibility(slotContainer);
	}
};

const schedulePersistentMismatchValidation = (
	logError: LogErrorFn,
	lastEvent: ISlotRenderEndedEvent,
): void => {
	const currentToken = ++validationToken;

	// Validate on the next render frame.
	requestAnimationFrame(() => {
		if (currentToken !== validationToken) return;

		const nonEmptyAdsFromEvents = renderedNonEmptySlots.size;
		const visibleAdsInDom = countVisibleAdsInDom();
		const mismatchStatus: IMismatchStatus = {
			nonEmptyAdsFromEvents,
			visibleAdsInDom,
		};

		if (nonEmptyAdsFromEvents === visibleAdsInDom) {
			lastLoggedMismatchStatus = undefined;
			return;
		}

		if (isSameMismatchStatus(lastLoggedMismatchStatus, mismatchStatus)) return;

		lastLoggedMismatchStatus = mismatchStatus;

		logError('GAM slotRenderEnded mismatch in OmniSearch', {
			nonEmptyAdsFromEvents,
			visibleAdsInDom,
			lastEvent: {
				slotId: lastEvent.slot?.getSlotElementId?.(),
				isEmpty: lastEvent.isEmpty,
				size: lastEvent.size,
				advertiserId: lastEvent.advertiserId,
				lineItemId: lastEvent.lineItemId,
				creativeId: lastEvent.creativeId,
			},
		});
	});
};

export const attachSlotRenderEnded = (
	pubads: IPubAds | undefined,
	logError: LogErrorFn,
): void => {
	if (hasSlotRenderEndedListener || !pubads?.addEventListener) return;

	hasSlotRenderEndedListener = true;

	pubads.addEventListener('slotRenderEnded', (event: ISlotRenderEndedEvent) => {
		const slotId = event.slot?.getSlotElementId?.();
		applyEmptySlotVisibility(event);

		if (slotId) {
			if (event.isEmpty === false) {
				renderedNonEmptySlots.add(slotId);
			} else if (event.isEmpty === true) {
				renderedNonEmptySlots.delete(slotId);
			}
		}

		schedulePersistentMismatchValidation(logError, event);
	});
};